import { Link } from 'react-router-dom';
import { siteInfo, testimonials } from '@/mocks/comics';
import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';

export default function About() {
  const [contactStatus, setContactStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [contactMsg, setContactMsg] = useState('');

  const handleContact = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setContactStatus('success');
      setContactMsg('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }
    setContactStatus('loading');
    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'company_alt') body.append(key, value as string);
      });
      const res = await fetch('https://readdy.ai/api/form/d9pe21hn2trhb35gvdm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setContactStatus('success');
        setContactMsg('感谢您的留言，我们会尽快回复！');
        form.reset();
      } else {
        throw new Error(parsed?.meta?.message || '提交失败');
      }
    } catch (err: unknown) {
      setContactStatus('error');
      setContactMsg(err instanceof Error ? err.message : '提交失败，请稍后重试。');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    'name': '关于韩漫之家',
    'description': siteInfo.description,
    'url': 'https://www.hanmanzhijia.com/about',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩漫之家',
      'url': 'https://www.hanmanzhijia.com',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'https://www.hanmanzhijia.com' },
      { '@type': 'ListItem', 'position': 2, 'name': '关于我们', 'item': 'https://www.hanmanzhijia.com/about' },
    ],
  };

  return (
    <>
      <SEOHead
        title="关于我们"
        description={siteInfo.description}
        keywords="韩漫之家,关于韩漫之家,免费韩漫,韩漫在线阅读,漫画网站介绍"
        canonicalUrl="/about"
        schema={aboutSchema}
      />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors no-underline cursor-pointer">首页</Link>
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <span className="text-foreground-700 font-medium">关于我们</span>
          </nav>
        </div>
      </div>

      {/* Brand Intro Hero */}
      <section className="py-12 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-primary-100 text-primary-600">
              <i className="ri-book-open-line text-3xl"></i>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-5 font-heading">关于韩漫之家</h1>
            <p className="text-foreground-600 leading-relaxed text-sm md:text-base">
              {siteInfo.name}成立于{siteInfo.established}年，是一个专注于<strong>韩国漫画</strong>的免费在线阅读平台。
              作为国内领先的<strong>免费韩漫大全</strong>网站，我们致力于为广大的漫画爱好者提供最便捷的
              <strong>韩漫在线阅读</strong>体验。通过简单的网址即可访问海量优质韩漫资源，极大简化您的找书流程。
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: 'ri-book-2-line', value: siteInfo.comicCount, label: '漫画资源' },
              { icon: 'ri-user-line', value: siteInfo.dailyVisitors, label: '日访问用户' },
              { icon: 'ri-eye-line', value: siteInfo.totalViews, label: '累计阅读量' },
              { icon: 'ri-calendar-check-line', value: '每日更新', label: '更新频率' },
            ].map((stat, idx) => (
              <div key={idx} className="text-center p-5 bg-white rounded-xl">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div className="text-xl md:text-2xl font-bold text-foreground-950 mb-1 font-heading">{stat.value}</div>
                <div className="text-xs text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10 font-heading">
            我们的理念
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-shield-star-line',
                title: '品质优先',
                desc: '我们精心筛选每一部收录的韩漫作品，确保内容的优质与正版，为读者提供最佳的阅读材料。',
              },
              {
                icon: 'ri-rocket-line',
                title: '极速体验',
                desc: '优化的页面加载速度和简洁的界面设计，让您在任何设备上都能享受流畅的在线阅读体验。',
              },
              {
                icon: 'ri-heart-pulse-line',
                title: '用户至上',
                desc: '始终以读者需求为核心，持续优化功能和内容，用心打造最贴心的韩漫阅读平台。',
              },
            ].map((value, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 text-center">
                <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                  <i className={`${value.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-bold text-foreground-900 mb-2 font-heading">{value.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation / Testimonials */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10 font-heading">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((t, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                    <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                    <div className="flex items-center gap-0.5 mt-0.5">
                      {Array.from({ length: 5 }, (_, i) => (
                        <div key={i} className="w-3 h-3 flex items-center justify-center">
                          <i className={`text-[10px] ${i < t.rating ? 'ri-star-fill text-yellow-400' : 'ri-star-line text-foreground-300'}`}></i>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{t.comment}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3 font-heading">联系我们</h2>
          <p className="text-sm text-foreground-500 text-center mb-8">
            有任何问题或建议？欢迎给我们留言
          </p>

          <form
            onSubmit={handleContact}
            data-readdy-form
            className="bg-white rounded-xl p-6 md:p-8 flex flex-col gap-5"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-name" className="text-xs font-medium text-foreground-700">姓名</label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  required
                  className="px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-email" className="text-xs font-medium text-foreground-700">邮箱</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  required
                  className="px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-700">主题</label>
              <input
                id="contact-subject"
                type="text"
                name="subject"
                required
                className="px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                placeholder="请输入留言主题"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-message" className="text-xs font-medium text-foreground-700">留言内容</label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="px-4 py-2.5 rounded-lg border border-background-300 text-sm bg-white text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                placeholder="请描述您的问题或建议（限500字）"
              />
              <span className="text-xs text-foreground-400 self-end">最多500个字符</span>
            </div>
            <input
              type="text"
              name="company_alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
              className="visually-hidden"
            />
            <button
              type="submit"
              disabled={contactStatus === 'loading'}
              className="w-full py-3 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60"
            >
              {contactStatus === 'loading' ? '提交中...' : '发送留言'}
            </button>

            {contactStatus !== 'idle' && (
              <p className={`text-sm text-center ${contactStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                {contactMsg}
              </p>
            )}
          </form>

          <div className="mt-10 text-center">
            <h3 className="text-sm font-medium text-foreground-700 mb-3 font-heading">其他联系方式</h3>
            <div className="flex items-center justify-center gap-8">
              <div className="flex items-center gap-2 text-sm text-foreground-600">
                <div className="w-5 h-5 flex items-center justify-center"><i className="ri-mail-line text-foreground-500"></i></div>
                {siteInfo.email}
              </div>
              <div className="flex items-center gap-3">
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="微博">
                  <i className="ri-weibo-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="微信">
                  <i className="ri-wechat-line"></i>
                </a>
                <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="QQ">
                  <i className="ri-qq-line"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
    </>
  );
}